import { useState } from 'react'
import { FormDrawer, type FormFieldDef } from '../ui/FormDrawer'
import { useEnumOptions } from '../lib/enums'
import { useApi, useAction } from '../lib/hooks'
import { api, session } from '../lib/api'
import { money, num, shortDate, titleCase } from '../lib/format'
import { Banner, Empty, ErrorNote, Kpi, Loading, Panel, Pill } from '../ui/kit'
import { against } from '../lib/format'

interface Density {
  corridorId: string; code: string; name: string; targetDrops: number; isOpenForSales: boolean
  vanDays: number; drops: number; deliveredKg: number
  dropsPerVanDay: number | null; kgPerVanDay: number | null; avgFillPct: number | null
  routeCost: number; lastMileCostPerKg: number | null; buyers: number
}

interface Trip {
  id: string; code: string; tripDate: string; tripType: string; status: string
  registration: string | null; capacityKg: number | null; vehicleLabel: string | null
  driverName: string | null; corridorName: string | null; depotName: string | null
  plannedKg: number; loadedKg: number; deliveredKg: number; fillPct: number | null
  plannedStops: number; completedStops: number
  hasBackhaul: boolean; backhaulRevenue: number; cessCost: number; fuelCost: number | null
  costPerKg: number | null
}

interface Rejection {
  id: string; code: string; rejectedKg: number; reasonCode: string; reasonNote: string | null
  rejectedAt: string; rejectedByName: string | null
  returnedAt: string | null; returnWeightKg: number | null
  disposition: string; resoldPricePerKg: number | null; originalPricePerKg: number
  capturedLoss: number | null; buyerName: string; lotCode: string; cropName: string
  awaitingReturn: boolean; valueAtRisk: number
}

interface OrderForTrip {
  id: string; code: string; status: string
  buyerName: string; corridorName: string | null
  deliveryDate: string; orderedKg: number; allocatedPct: number | null
}

interface Driver { id: string; fullName: string; msisdn: string; tripsToday: number }
interface Vehicle { id: string; registration: string; label: string | null; capacityKg: number }
interface Depot2 { id: string; name: string }
interface Corridor2 { id: string; name: string }
interface RefBundle { vehicles: Vehicle[]; depots: Depot2[]; corridors: Corridor2[] }

export function Logistics() {
  const density = useApi<Density[]>('/ops/density')
  const trips = useApi<Trip[]>('/trips')
  const [building, setBuilding] = useState(false)
  const [tripBanner, setTripBanner] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)
  const canPlan = session.has('admin', 'ops')
  const rejections = useApi<Rejection[]>('/rejections?openOnly=true')

  const d = density.data ?? []
  const worst = d.filter((c) => c.vanDays > 0).sort((a, b) => (a.dropsPerVanDay ?? 0) - (b.dropsPerVanDay ?? 0))[0]
  const awaitingReturn = rejections.data?.filter((r) => r.awaitingReturn) ?? []

  const receiveReturn = useAction(async (id: string, weight: number) => {
    await api.post(`/rejections/${id}/return`, { returnWeightKg: weight })
    rejections.reload()
  })

  return (
    <main className="page">
      <div className="page-head">
        <h1>Deliveries</h1>
        <p>
          Two things decide whether delivering is worth it: how many stops a van makes in a day, and
          how full it goes out. Produce margins are too thin to run half-empty vans.
        </p>
      </div>

      <div className="stack">
        {worst && worst.dropsPerVanDay !== null && worst.dropsPerVanDay < worst.targetDrops && (
          <Banner tone="warn" title={`${worst.name} is running at ${worst.dropsPerVanDay} drops per van-day`}>
            Below the {worst.targetDrops} target. Either fill the corridor before opening another, or
            stop running it — a thin route loses money on every drop and no operational excellence
            fixes that.
          </Banner>
        )}

        <Panel
          title="How busy each area is"
          meta="never reported as a company average — the average hides the thin route"
        >
          {density.error && <ErrorNote error={density.error} onRetry={density.reload} />}
          {density.loading && <Loading rows={3} />}
          {d.length === 0 && !density.loading && (
            <Empty title="No deliveries in this period">
              Once vans start running you will see here which areas are worth delivering to.
            </Empty>
          )}
          {d.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Area</th><th className="n">Van trips</th><th className="n">Stops</th>
                    <th className="n">Stops per van</th><th className="n">Kg per van</th>
                    <th className="n">Avg how full</th><th className="n">Delivery cost / kg</th>
                    <th className="n">Buyers</th><th>Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {d.map((c) => {
                    const thin = c.dropsPerVanDay !== null && c.dropsPerVanDay < c.targetDrops
                    return (
                      <tr key={c.corridorId} className={thin ? 'attention' : ''}>
                        <td className="strong">{c.name}</td>
                        <td className="n">{c.vanDays}</td>
                        <td className="n">{c.drops}</td>
                        <td className="n">
                          <span className={thin ? 'crit-t' : 'ok-t'}>{c.dropsPerVanDay ?? '—'}</span>
                          <span className="faint"> / {c.targetDrops}</span>
                        </td>
                        <td className="n">{num(c.kgPerVanDay)}</td>
                        <td className="n">{c.avgFillPct === null ? '—' : `${c.avgFillPct}%`}</td>
                        <td className="n">{c.lastMileCostPerKg === null ? '—' : Number(c.lastMileCostPerKg).toFixed(2)}</td>
                        <td className="n faint">{c.buyers}</td>
                        <td>
                          <Pill tone={c.isOpenForSales ? 'ok' : 'warn'}>
                            {c.isOpenForSales ? 'Open' : 'Closed'}
                          </Pill>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel
          title="Rejections"
          meta={
            awaitingReturn.length > 0
              ? <span className="crit-t">{awaitingReturn.length} awaiting physical return</span>
              : 'all returns received'
          }
        >
          {rejections.loading && <Loading rows={3} />}
          {rejections.data?.length === 0 && (
            <Empty title="Nothing sent back">
              Either no buyer turned anything away, or everything they did has come back, been
              weighed again, and been dealt with.
            </Empty>
          )}
          {rejections.data && rejections.data.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Ref</th><th>Buyer</th><th>Collection</th><th className="n">Rejected</th>
                    <th className="n">At risk</th><th>Returned</th><th>What happened to it</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {rejections.data.map((r) => (
                    <tr key={r.id} className={r.awaitingReturn ? 'attention' : ''}>
                      <td className="code strong">{r.code}</td>
                      <td className="truncate">{r.buyerName}</td>
                      <td className="code">{r.lotCode}</td>
                      <td className="n">{num(r.rejectedKg, 1)} kg</td>
                      <td className="n">{money(r.valueAtRisk, { currency: false })}</td>
                      <td>
                        {r.returnWeightKg === null
                          ? <Pill tone="crit" dot={false}>not back</Pill>
                          : <>{num(r.returnWeightKg, 1)} kg</>}
                      </td>
                      <td>{titleCase(r.disposition)}</td>
                      <td>
                        {r.awaitingReturn && (
                          <button
                            className="btn quiet"
                            style={{ minHeight: 26, padding: '2px 10px', fontSize: 'var(--fs-xs)' }}
                            disabled={receiveReturn.busy}
                            onClick={() => {
                              const w = window.prompt(
                                `Weigh the returned goods. Expected about ${Number(r.rejectedKg).toFixed(1)} kg.`,
                                Number(r.rejectedKg).toFixed(1),
                              )
                              const n = Number(w)
                              if (n > 0) void receiveReturn.run(r.id, n)
                            }}
                          >
                            Receive &amp; weigh
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {receiveReturn.error && (
            <div className="panel-body"><div className="error-text">{receiveReturn.error.message}</div></div>
          )}
        </Panel>

        <Panel
          title="Trips"
          meta={canPlan && <button className="btn" onClick={() => setBuilding(true)}>Build a trip</button>}
        >
          {tripBanner && (
            <div className="panel-body" style={{ paddingBottom: 0 }}>
              <Banner tone={tripBanner.tone === 'ok' ? 'info' : 'crit'} title={tripBanner.text}>{null}</Banner>
            </div>
          )}
          {trips.loading && <Loading rows={4} />}
          {trips.error && <ErrorNote error={trips.error} onRetry={trips.reload} />}
          {trips.data?.length === 0 && (
            <Empty
              title="No trips yet"
              action={canPlan ? <button className="btn" onClick={() => setBuilding(true)}>Build a trip</button> : undefined}
            >
              Build a trip from orders that have stock set aside, and the driver will see the run
              on their phone.
            </Empty>
          )}
          {trips.data && trips.data.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Trip</th><th className="n">Date</th><th>Type</th><th>Vehicle</th><th>Driver</th>
                    <th className="n">Stops</th><th className="n">Planned</th><th className="n">How full</th>
                    <th className="n">Cess</th><th className="n">Cost / kg</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {trips.data.map((t) => (
                    <tr key={t.id}>
                      <td className="code strong">{t.code}</td>
                      <td className="n">{shortDate(t.tripDate)}</td>
                      <td>{titleCase(t.tripType)}</td>
                      <td className="mono" style={{ fontSize: 'var(--fs-xs)' }}>{t.registration ?? '—'}</td>
                      <td className="truncate">{t.driverName ?? '—'}</td>
                      <td className="n">{t.completedStops} / {t.plannedStops}</td>
                      <td className="n">{num(t.plannedKg)} kg</td>
                      <td className="n">
                        <span className={t.fillPct !== null && t.fillPct < 85 ? 'warn-t' : 'ok-t'}>
                          {t.fillPct === null ? '—' : `${t.fillPct}%`}
                        </span>
                      </td>
                      <td className="n">{money(t.cessCost, { currency: false })}</td>
                      <td className="n">{t.costPerKg === null ? '—' : Number(t.costPerKg).toFixed(2)}</td>
                      <td><Pill tone={t.status === 'completed' ? 'ok' : 'info'}>{titleCase(t.status)}</Pill></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {d.length > 0 && (
          <div className="kpi-grid">
            <Kpi
              label="Total stops"
              value={d.reduce((s, c) => s + c.drops, 0)}
              sub="across all corridors this week"
            />
            <Kpi
              label="Delivered"
              value={num(d.reduce((s, c) => s + Number(c.deliveredKg ?? 0), 0))}
              unit="kg"
            />
            <Kpi
              label="Cost to run the route"
              value={money(d.reduce((s, c) => s + Number(c.routeCost ?? 0), 0), { compact: true })}
              sub="fuel, vehicle, cess, less backhaul"
            />
            <Kpi
              label="Areas below target"
              value={d.filter((c) => c.dropsPerVanDay !== null && c.dropsPerVanDay < c.targetDrops).length}
              tone={
                against(
                  -d.filter((c) => c.dropsPerVanDay !== null && c.dropsPerVanDay < c.targetDrops).length,
                  0,
                )
              }
              sub="each one loses money on every drop"
            />
          </div>
        )}
      </div>

      {building && (
        <BuildTrip
          onClose={() => setBuilding(false)}
          onDone={(code, stops) => {
            setBuilding(false)
            setTripBanner({
              tone: 'ok',
              text: `${code} planned with ${stops} stop${stops === 1 ? '' : 's'}. The driver sees it on their run.`,
            })
            trips.reload()
          }}
        />
      )}
    </main>
  )
}

// ---------------------------------------------------------------- build a trip
/**
 * A trip turns allocated orders into a driver's run. Only allocated orders are
 * offered: a trip built from orders with no lots behind them is a van sent out
 * half empty, and van fill is the number the last mile lives or dies on.
 */
function BuildTrip({ onClose, onDone }: {
  onClose: () => void
  onDone: (code: string, stops: number) => void
}) {
  const tripTypes = useEnumOptions('trip_type', {
    last_mile: 'Last mile - to buyers',
    primary_haul: 'Primary haul - to a depot',
  })
  const orders = useApi<OrderForTrip[]>('/orders?status=allocated')
  const drivers = useApi<Driver[]>('/reference/drivers')
  const ref = useApi<RefBundle>('/reference/bundle', { cacheKey: 'reference.bundle' })

  const [values, setValues] = useState<Record<string, unknown>>({
    tripDate: new Date().toISOString().slice(0, 10),
    tripType: 'last_mile',
  })
  const [picked, setPicked] = useState<string[]>([])
  const set = (name: string, v: unknown) => setValues((s) => ({ ...s, [name]: v }))

  const rows = orders.data ?? []
  const vehicle = (ref.data?.vehicles ?? []).find((v) => v.id === values.vehicleId)

  const load = rows
    .filter((o) => picked.includes(o.id))
    .reduce((s, o) => s + Number(o.orderedKg ?? 0), 0)
  const fill = vehicle && Number(vehicle.capacityKg) > 0
    ? (load / Number(vehicle.capacityKg)) * 100
    : null

  const fields: FormFieldDef[] = [
    { name: 'tripDate', label: 'Date', type: 'date', required: true, half: true },
    { name: 'tripType', label: 'Type', type: 'select', required: true, half: true, options: tripTypes },
    {
      name: 'vehicleId', label: 'Vehicle', type: 'select', half: true,
      options: (ref.data?.vehicles ?? []).map((v) => ({
        value: v.id,
        label: `${v.registration}${v.label ? ` - ${v.label}` : ''} (${v.capacityKg} kg)`,
      })),
    },
    {
      name: 'driverUserId', label: 'Driver', type: 'select', half: true,
      options: (drivers.data ?? []).map((d) => ({
        value: d.id,
        label: `${d.fullName}${d.tripsToday > 0 ? ` - ${d.tripsToday} already today` : ''}`,
      })),
    },
    {
      name: 'corridorId', label: 'Corridor', type: 'select', half: true,
      options: (ref.data?.corridors ?? []).map((c) => ({ value: c.id, label: c.name })),
    },
    {
      name: 'depotId', label: 'Leaving from', type: 'select', half: true,
      options: (ref.data?.depots ?? []).map((d) => ({ value: d.id, label: d.name })),
    },
  ]

  async function submit() {
    if (picked.length === 0) throw new Error('Pick the orders going on this van.')
    // The server answers with the trip plus any warnings it wants a person to see -
    // an ethylene producer loaded next to something sensitive spoils the whole van.
    const res = await api.post<{
      trip: { code: string; plannedStops: number }
      warnings?: Array<{ code: string; message: string }>
    }>('/trips', {
      tripDate: values.tripDate,
      tripType: values.tripType,
      depotId: values.depotId || null,
      corridorId: values.corridorId || null,
      vehicleId: values.vehicleId || null,
      driverUserId: values.driverUserId || null,
      orderIds: picked,
    })
    if (res.warnings?.length) {
      window.alert(res.warnings.map((w) => w.message).join('\n\n'))
    }
    onDone(res.trip.code, res.trip.plannedStops ?? picked.length)
  }

  return (
    <FormDrawer
      wide kind="Plan" title="Trip" submitLabel="Build the run"
      fields={fields} values={values} onChange={set}
      onSubmit={submit} onClose={onClose}
      footer={
        <>
          {picked.length} stop{picked.length === 1 ? '' : 's'} - <b>{num(load, 0)} kg</b>
          {fill !== null && (
            <span className={fill < 85 ? ' warn-t' : ' ok-t'}> - {fill.toFixed(0)}% full</span>
          )}
        </>
      }
    >
      {fill !== null && fill > 100 && (
        <Banner tone="crit" title="That will not fit in the van">
          {num(load, 0)} kg loaded into a van that holds {num(Number(vehicle?.capacityKg ?? 0), 0)} kg.
          Take a stop off, or send a second van.
        </Banner>
      )}

      {fill !== null && fill < 85 && fill > 0 && (
        <Banner tone="warn" title="This van is going out less than 85% full">
          The trip costs about the same whether the van is full or half empty, so another stop on
          this run is nearly free money.
        </Banner>
      )}

      <div className="micro" style={{ marginTop: 'var(--sp-7)', marginBottom: 'var(--sp-3)' }}>
        Orders with stock set aside
      </div>

      {orders.loading && <Loading rows={3} />}
      {rows.length === 0 && !orders.loading && (
        <p className="hint">
          Nothing ready. An order needs stock set aside for it on the Orders page before it can go
          on a van.
        </p>
      )}

      {rows.map((o) => {
        const on = picked.includes(o.id)
        return (
          <label className="check-row" key={o.id}>
            <input type="checkbox" checked={on}
              onChange={() => setPicked((s) => on ? s.filter((x) => x !== o.id) : [...s, o.id])} />
            <span>
              <b>{o.buyerName}</b>
              <i>
                {o.code} - {num(o.orderedKg, 0)} kg - {shortDate(o.deliveryDate)}
                {o.corridorName ? ` - ${o.corridorName}` : ''}
              </i>
            </span>
          </label>
        )
      })}

      <p className="hint" style={{ marginTop: 'var(--sp-6)' }}>
        The driver visits the stops in the order you tick them, and can see the whole run before
        setting off.
      </p>
    </FormDrawer>
  )
}
